<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Productos</h1>
      <p class="text-gray-600 dark:text-gray-400">Catálogo de productos POS2</p>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <!-- Header / Filtros -->
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
        <div class="flex flex-wrap gap-2">
          <input
            v-model="filtros.buscar"
            @keyup.enter="buscar"
            type="text"
            placeholder="Buscar por nombre, ID o abreviatura..."
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 w-72"
          />
          <select
            v-model="filtros.activo"
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200"
          >
            <option value="">Todos</option>
            <option value="1">Activos</option>
            <option value="0">Inactivos</option>
          </select>
          <button @click="buscar" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
          <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </div>
        <button
          @click="openModal()"
          class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          Nuevo Producto
        </button>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-3 py-2">ID</th>
              <th class="px-3 py-2">Nombre</th>
              <th class="px-3 py-2">Abreviatura</th>
              <th class="px-3 py-2">Precio</th>
              <th class="px-3 py-2">Unidad</th>
              <th class="px-3 py-2">Tipo Comp.</th>
              <th class="px-3 py-2">Departamento</th>
              <th class="px-3 py-2 text-center">P.Term.</th>
              <th class="px-3 py-2 text-center">Activo</th>
              <th class="px-3 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="10" class="px-4 py-8 text-center text-gray-400">Cargando...</td>
            </tr>
            <template v-else>
              <tr
                v-for="prod in productos"
                :key="prod.id"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <td class="px-3 py-2 font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">{{ prod.producto_id }}</td>
                <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">{{ prod.nombre }}</td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">{{ prod.abreviatura }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-300">{{ prod.precio != null ? `$${Number(prod.precio).toFixed(2)}` : '—' }}</td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">
                  <span v-if="prod.unidad_medida" class="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{{ prod.unidad_medida.id_unidad_medida }}</span>
                </td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">{{ prod.tipo_componente?.nombre }}</td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">{{ prod.departamento?.nombre }}</td>
                <td class="px-3 py-2 text-center">
                  <span :class="prod.producto_terminado ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'" class="px-2 py-0.5 rounded-full text-xs font-semibold">
                    {{ prod.producto_terminado ? 'Sí' : 'No' }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span :class="prod.activo ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'" class="px-2 py-0.5 rounded-full text-xs font-semibold">
                    {{ prod.activo ? 'Sí' : 'No' }}
                  </span>
                </td>
                <td class="px-3 py-2">
                  <div class="flex gap-1">
                    <button @click="openModal(prod)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 p-1" title="Editar">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                    </button>
                    <button @click="eliminar(prod)" class="text-red-500 hover:text-red-700 dark:text-red-400 p-1" title="Eliminar">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="productos.length === 0">
                <td colspan="10" class="px-4 py-8 text-center text-gray-400">No se encontraron productos</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="pagination.total > 0" class="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }}
          a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
          de {{ pagination.total }} registros
        </div>
        <div class="flex gap-2">
          <button @click="cambiarPagina(pagination.current_page - 1)" :disabled="pagination.current_page <= 1" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Anterior</button>
          <span class="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
          <button @click="cambiarPagina(pagination.current_page + 1)" :disabled="pagination.current_page >= pagination.last_page" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>

    <!-- ==================== MODAL ==================== -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <!-- Header modal -->
        <div class="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 class="text-lg font-bold text-gray-900 dark:text-white">
            {{ editando ? 'Editar Producto' : 'Nuevo Producto' }}
          </h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Body modal scrollable -->
        <div class="overflow-y-auto px-6 py-4 flex-1">
          <form @submit.prevent="guardar" id="formProducto">

            <!-- Fila 1: ID + Clave SAT -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                  ID Producto <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.producto_id"
                  type="text"
                  maxlength="20"
                  :disabled="!!editando"
                  required
                  class="w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                  :class="editando ? 'border-gray-200 bg-gray-50 dark:bg-gray-600 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'"
                  placeholder="Ej: PROD-001"
                />
                <p v-if="formErrors.producto_id" class="text-red-500 text-xs mt-1">{{ formErrors.producto_id[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Clave SAT</label>
                <input
                  v-model="form.clave_sat"
                  type="text"
                  maxlength="20"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Ej: 43211706"
                />
                <p v-if="formErrors.clave_sat" class="text-red-500 text-xs mt-1">{{ formErrors.clave_sat[0] }}</p>
              </div>
            </div>

            <!-- Fila 2: Nombre -->
            <div class="mb-4">
              <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                Nombre <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.nombre"
                type="text"
                maxlength="100"
                required
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                placeholder="Nombre del producto"
              />
              <p v-if="formErrors.nombre" class="text-red-500 text-xs mt-1">{{ formErrors.nombre[0] }}</p>
            </div>

            <!-- Fila 3: Descripción -->
            <div class="mb-4">
              <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Descripción</label>
              <textarea
                v-model="form.descripcion"
                maxlength="255"
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white resize-none"
                placeholder="Descripción del producto..."
              ></textarea>
              <p v-if="formErrors.descripcion" class="text-red-500 text-xs mt-1">{{ formErrors.descripcion[0] }}</p>
            </div>

            <!-- Fila 4: Abreviatura + Precio -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Abreviatura</label>
                <input
                  v-model="form.abreviatura"
                  type="text"
                  maxlength="20"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Ej: PROD"
                />
                <p v-if="formErrors.abreviatura" class="text-red-500 text-xs mt-1">{{ formErrors.abreviatura[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Precio</label>
                <input
                  v-model="form.precio"
                  type="number"
                  min="0"
                  step="0.0001"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0.00"
                />
                <p v-if="formErrors.precio" class="text-red-500 text-xs mt-1">{{ formErrors.precio[0] }}</p>
              </div>
            </div>

            <!-- Fila 5: Unidad Medida + Tipo Componente + Departamento -->
            <div class="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Unidad de Venta</label>
                <select
                  v-model="form.unidad_medida_id"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                >
                  <option :value="null">— Sin unidad —</option>
                  <option v-for="u in catUnidades" :key="u.id" :value="u.id">{{ u.id_unidad_medida }} - {{ u.nombre }}</option>
                </select>
                <p v-if="formErrors.unidad_medida_id" class="text-red-500 text-xs mt-1">{{ formErrors.unidad_medida_id[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Tipo Componente</label>
                <select
                  v-model="form.tipo_componente_id"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                >
                  <option :value="null">— Sin tipo —</option>
                  <option v-for="t in catTipos" :key="t.id" :value="t.id">{{ t.nombre }}</option>
                </select>
                <p v-if="formErrors.tipo_componente_id" class="text-red-500 text-xs mt-1">{{ formErrors.tipo_componente_id[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Departamento</label>
                <select
                  v-model="form.departamento_id"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                >
                  <option :value="null">— Sin departamento —</option>
                  <option v-for="d in catDepartamentos" :key="d.id" :value="d.id">{{ d.nombre }}</option>
                </select>
                <p v-if="formErrors.departamento_id" class="text-red-500 text-xs mt-1">{{ formErrors.departamento_id[0] }}</p>
              </div>
            </div>

            <!-- Fila 6: Ancho + Alto -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Ancho Default</label>
                <input
                  v-model="form.ancho_default"
                  type="number"
                  min="0"
                  step="0.0001"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0.00"
                />
                <p v-if="formErrors.ancho_default" class="text-red-500 text-xs mt-1">{{ formErrors.ancho_default[0] }}</p>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Alto Default</label>
                <input
                  v-model="form.alto_default"
                  type="number"
                  min="0"
                  step="0.0001"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0.00"
                />
                <p v-if="formErrors.alto_default" class="text-red-500 text-xs mt-1">{{ formErrors.alto_default[0] }}</p>
              </div>
            </div>

            <!-- Fila 7: Flags -->
            <div class="grid grid-cols-3 gap-4 mb-2">
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                  Producto Terminado <span class="text-red-500">*</span>
                </label>
                <div class="flex gap-4 mt-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" :value="true" v-model="form.producto_terminado" class="accent-violet-600"/> <span class="text-sm">Sí</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" :value="false" v-model="form.producto_terminado" class="accent-violet-600"/> <span class="text-sm">No</span>
                  </label>
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Requiere Conversión</label>
                <div class="flex gap-4 mt-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" :value="true" v-model="form.requiere_conversion" class="accent-violet-600"/> <span class="text-sm">Sí</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" :value="false" v-model="form.requiere_conversion" class="accent-violet-600"/> <span class="text-sm">No</span>
                  </label>
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Activo</label>
                <div class="flex gap-4 mt-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" :value="true" v-model="form.activo" class="accent-violet-600"/> <span class="text-sm">Sí</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="radio" :value="false" v-model="form.activo" class="accent-violet-600"/> <span class="text-sm">No</span>
                  </label>
                </div>
              </div>
            </div>

          </form>
        </div>

        <!-- Footer modal -->
        <div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >Cancelar</button>
          <button
            type="submit"
            form="formProducto"
            :disabled="guardando"
            class="px-5 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
          >{{ guardando ? 'Guardando...' : (editando ? 'Actualizar' : 'Crear') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import Swal from 'sweetalert2'

const { get, post, put, delete: del, data } = useHttp()

// ─── Lista ────────────────────────────────────────────────────────────────────
const productos  = ref([])
const loading    = ref(false)
const filtros    = ref({ buscar: '', activo: '' })
const pagination = ref({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

// ─── Catálogos para selects ───────────────────────────────────────────────────
const catUnidades     = ref([])
const catTipos        = ref([])
const catDepartamentos = ref([])

// ─── Modal ────────────────────────────────────────────────────────────────────
const showModal  = ref(false)
const editando   = ref(null)
const guardando  = ref(false)
const formErrors = ref({})

const emptyForm = () => ({
  producto_id:         '',
  clave_sat:           '',
  nombre:              '',
  descripcion:         '',
  abreviatura:         '',
  producto_terminado:  false,
  requiere_conversion: false,
  precio:              '',
  unidad_medida_id:    null,
  tipo_componente_id:  null,
  departamento_id:     null,
  ancho_default:       '',
  alto_default:        '',
  activo:              true,
})

const form = ref(emptyForm())

// ─── Carga de datos ───────────────────────────────────────────────────────────
const cargar = async (pagina = 1) => {
  try {
    loading.value = true
    const params = new URLSearchParams({ page: pagina, per_page: pagination.value.per_page })
    if (filtros.value.buscar) params.append('buscar', filtros.value.buscar)
    if (filtros.value.activo !== '') params.append('activo', filtros.value.activo)
    await get(`/pos2/productos?${params}`)
    productos.value = data.value.data
    pagination.value = {
      current_page: data.value.current_page,
      last_page:    data.value.last_page,
      per_page:     data.value.per_page,
      total:        data.value.total,
    }
  } finally {
    loading.value = false
  }
}

const cargarCatalogos = async () => {
  const [resU, resT, resD] = await Promise.all([
    get('/pos2/unidades-medida/todas').then(() => data.value),
    get('/pos2/tipos-componente?per_page=999').then(() => data.value?.data ?? data.value),
    get('/pos2/departamentos?per_page=999').then(() => data.value?.data ?? data.value),
  ])
  catUnidades.value      = resU
  catTipos.value         = resT
  catDepartamentos.value = resD
}

const buscar        = () => cargar(1)
const limpiar       = () => { filtros.value = { buscar: '', activo: '' }; cargar(1) }
const cambiarPagina = (p) => { if (p >= 1 && p <= pagination.value.last_page) cargar(p) }

// ─── Modal ────────────────────────────────────────────────────────────────────
const openModal = (prod = null) => {
  editando.value  = prod
  formErrors.value = {}
  form.value = prod ? {
    producto_id:         prod.producto_id,
    clave_sat:           prod.clave_sat           ?? '',
    nombre:              prod.nombre,
    descripcion:         prod.descripcion         ?? '',
    abreviatura:         prod.abreviatura         ?? '',
    producto_terminado:  prod.producto_terminado,
    requiere_conversion: prod.requiere_conversion,
    precio:              prod.precio              ?? '',
    unidad_medida_id:    prod.unidad_medida_id    ?? null,
    tipo_componente_id:  prod.tipo_componente_id  ?? null,
    departamento_id:     prod.departamento_id     ?? null,
    ancho_default:       prod.ancho_default       ?? '',
    alto_default:        prod.alto_default        ?? '',
    activo:              prod.activo,
  } : emptyForm()
  showModal.value = true
}

const closeModal = () => {
  showModal.value  = false
  editando.value   = null
  formErrors.value = {}
}

const guardar = async () => {
  try {
    guardando.value = true
    formErrors.value = {}
    const payload = {
      ...form.value,
      precio:        form.value.precio        !== '' ? Number(form.value.precio)        : null,
      ancho_default: form.value.ancho_default !== '' ? Number(form.value.ancho_default) : null,
      alto_default:  form.value.alto_default  !== '' ? Number(form.value.alto_default)  : null,
    }
    if (editando.value) {
      await put(`/pos2/productos/${editando.value.id}`, payload)
      Swal.fire('¡Éxito!', 'Producto actualizado correctamente', 'success')
    } else {
      await post('/pos2/productos', payload)
      Swal.fire('¡Éxito!', 'Producto creado correctamente', 'success')
    }
    closeModal()
    cargar(pagination.value.current_page)
  } catch (err) {
    formErrors.value = err.response?.data?.errors ?? {}
    if (!Object.keys(formErrors.value).length) {
      Swal.fire('Error', err.response?.data?.message ?? 'Error al guardar', 'error')
    }
  } finally {
    guardando.value = false
  }
}

const eliminar = async (prod) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Deseas eliminar "${prod.nombre}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`/pos2/productos/${prod.id}`)
      cargar(pagination.value.current_page)
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar', 'error')
    }
  }
}

onMounted(() => {
  cargar()
  cargarCatalogos()
})
</script>
